import * as React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import {
    Typography, Drawer, Fab, withWidth,
} from '@material-ui/core';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import classnames from 'classnames';

import { useEffect, useState } from 'react';
import BreadCrumb from './BreadCrumb';
import SideMenu from './SideMenu';
import { headerHeight, scrollerWidth } from '../../app/style/theme';
import { CloseIcon, ListIcon } from '../icons';
import { contentWidth } from './Layout';

export const sideMenuWidth = 300;

const styles = theme => ({
    root: {
        maxWidth: contentWidth,
        margin: theme.spacing(4, 'auto'),
        minHeight: `calc(100vh - ${headerHeight}px)`,
        padding: theme.spacing(0, 12),

        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(0, 4.5),
        },

        [theme.breakpoints.down('xs')]: {
            marginTop: 0,
            padding: theme.spacing(0, 1.5),
        },

        '@media print': {
            minHeight: 0,
        },
    },
    withSideMenu: {
        maxWidth: 650,
        padding: theme.spacing(0, 1),

        [theme.breakpoints.up('md')]: {
            marginRight: `calc((100% - ${theme.breakpoints.values.md - scrollerWidth}px)/2 + ${sideMenuWidth}px + ${theme.spacing(2)}px)`,
        },
    },
    drawer: {
        '& .MuiDrawer-paperAnchorRight': {
            top: headerHeight,
            height: `calc(100% - ${headerHeight})`,
            minWidth: 280,
            maxWidth: '80%',

            [theme.breakpoints.up('md')]: {
                width: `calc((100% - ${theme.breakpoints.values.md - scrollerWidth}px)/2 + ${sideMenuWidth}px)`,
            },
        },
        '@media print': {
            display: 'none',
        },
    },
    openSideMenuFab: {
        position: 'fixed',
        right: theme.spacing(2),
        bottom: theme.spacing(2),
        zIndex: 100,

        [theme.breakpoints.up('md')]: {
            display: 'none',
        },

        '@media print': {
            display: 'none',
        },
    },
    sideMenu: {
        display: 'flex',
        flexDirection: 'column',
        height: `calc(100% - ${headerHeight}px)`,
        background: theme.palette.grey[100],
    },
    sideMenuContent: {
        flex: '1 1 auto',
    },
    actions: {
        padding: theme.spacing(2),
        display: 'flex',
        justifyContent: 'flex-end',
    },
    closeSideMenuFab: {
        marginLeft: theme.spacing(2),
    },
    topPadding: {
        marginBottom: theme.spacing(2),

        [theme.breakpoints.down('xs')]: {
            marginBottom: theme.spacing(1),
        },
    },
    breadcrumb: {
        marginTop: theme.spacing(2),

        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(0, 1),
        },
    },
    image: {
        marginBottom: theme.spacing(1),
    },
    title: {
        marginBottom: theme.spacing(1),
        outline: 'none',
        color: theme.palette.primary.main,
    },
    icon: {
        position: 'absolute',
        transition: 'opacity 0.2s ease-in-out',
        opacity: 1,
    },
    hiddenIcon: {
        opacity: 0,
    },
});

const getMaxPageYOffset = () => Math.max(
    document.body.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.clientHeight,
    document.documentElement.scrollHeight,
    document.documentElement.offsetHeight,
) - window.innerHeight;

const PageLayout = ({
    classes, breadcrumb, title, children, translate, progress, sideMenu, task, clientConfig, sideContent, width,
}) => {
    const [maxPageYOffset, setMaxPageYOffset] = useState(Infinity);
    const [pageYOffset, setPageYOffset] = useState(window.pageYOffset);
    const [showSideMenu, setShowSideMenu] = useState(false);

    const activeSideMenuDivRef = React.useRef();
    const titleRef = React.useRef();

    const handleResize = () => {
        setMaxPageYOffset(getMaxPageYOffset());
    };

    const handleScroll = () => {
        setMaxPageYOffset(getMaxPageYOffset());
        setPageYOffset(window.pageYOffset);
    };

    useEffect(() => {
        if (sideMenu) {
            window.addEventListener('resize', handleResize);
            window.addEventListener('scroll', handleScroll);
        }

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [sideMenu]);

    useEffect(() => {
        if (showSideMenu && activeSideMenuDivRef.current) {
            activeSideMenuDivRef.current.children[0].focus();
        } else if (titleRef.current) {
            titleRef.current.focus();
        }
    }, [showSideMenu]);

    const closeSideMenu = () => {
        setShowSideMenu(false);
    };

    const openSideMenu = () => {
        setShowSideMenu(true);
    };

    const labelClasses = task && task.taskLabels ? task.taskLabels.map(d => `${classes.root}_${d}`) : [];

    const showSideMenuButton = pageYOffset > 60 && maxPageYOffset - pageYOffset > 100;
    const hasSideMenu = sideMenu || sideContent;
    const isSmallScreen = width === 'xs' || width === 'sm';

    return (
        <>
            {hasSideMenu && (
                <>
                    {showSideMenuButton && (
                        <Fab
                            className={classes.openSideMenuFab}
                            onClick={openSideMenu}
                            size="medium"
                            aria-label={translate('openMenu')}
                            aria-controls="pageIndex-menu"
                            aria-haspopup="true"
                        >
                            <ListIcon />
                        </Fab>
                    )}

                    <Drawer
                        id="pageIndex-menu"
                        className={classes.drawer}
                        variant={isSmallScreen ? 'temporary' : 'persistent'}
                        anchor="right"
                        open={showSideMenu || !isSmallScreen}
                        onClose={closeSideMenu}
                    >
                        <div className={classes.sideMenu}>
                            {sideContent && (
                                <div
                                    className={classes.sideMenuContent}
                                >
                                    {sideContent}
                                </div>
                            )}

                            {sideMenu && (
                                <SideMenu
                                    className={classes.sideMenuContent}
                                    menu={sideMenu}
                                    activeSideMenuDivRef={activeSideMenuDivRef}
                                />
                            )}

                            {isSmallScreen && (
                                <div className={classes.actions}>
                                    <Fab onClick={closeSideMenu} className={classes.closeSideMenuFab} aria-label={translate('close')} size="medium">
                                        <CloseIcon />
                                    </Fab>
                                </div>
                            )}
                        </div>
                    </Drawer>
                </>
            )}

            <div className={classnames(classes.root, ...labelClasses, {
                [classes.withSideMenu]: hasSideMenu,
            })}
            >
                <div className={classes.topPadding}>
                    {clientConfig.appSetup.features.breadcrumb && breadcrumb && (
                        <BreadCrumb levels={breadcrumb} className={classes.breadcrumb} progress={progress} task={task} openSideMenu={hasSideMenu && openSideMenu} />
                    )}
                </div>

                {title && (
                    <Typography variant="h5" className={classes.title} ref={titleRef} tabIndex="-1">
                        {title}
                    </Typography>
                )}

                {children}
            </div>
        </>
    );
};

PageLayout.propTypes = {
    classes: PropTypes.object.isRequired,
    clientConfig: PropTypes.object.isRequired,
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
    sideContent: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
    translate: PropTypes.func.isRequired,
    breadcrumb: PropTypes.array,
    sideMenu: PropTypes.object,
    title: PropTypes.string,
    progress: PropTypes.number,
    task: PropTypes.object,
    width: PropTypes.oneOf(['lg', 'md', 'sm', 'xl', 'xs']).isRequired,
};

PageLayout.defaultProps = {
    breadcrumb: null,
    title: null,
    progress: null,
    sideMenu: null,
    task: null,
    sideContent: null,
};

const mapStateToProps = state => ({
    translate: getTranslate(state.localize),
    clientConfig: state.auth.clientConfig,
});

export default connect(mapStateToProps)(withWidth()(withStyles(styles)(PageLayout)));
